import { Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PortalLifecycleStatus } from "../../../../autogen/bff-api";
import { FilterChip } from "../../../../common/components/FilterChip";

export type PortalListFilter = "OPEN" | "IN_PROGRESS" | "CLOSED";

export const PORTAL_LIST_FILTER_ORDER: PortalListFilter[] = ["OPEN", "IN_PROGRESS", "CLOSED"];

export const PORTAL_LIST_FILTER_STATUSES: Record<PortalListFilter, PortalLifecycleStatus[]> = {
  OPEN: [PortalLifecycleStatus.Open],
  IN_PROGRESS: [PortalLifecycleStatus.DeadlinePassed, PortalLifecycleStatus.UnderEvaluation],
  CLOSED: [PortalLifecycleStatus.Awarded, PortalLifecycleStatus.Cancelled, PortalLifecycleStatus.Concluded],
};

export const countFor = (
  filter: PortalListFilter,
  counts: Partial<Record<PortalLifecycleStatus, number>>,
): number => PORTAL_LIST_FILTER_STATUSES[filter].reduce((sum, status) => sum + (counts[status] ?? 0), 0);

export const usePortalListHeading = () => {
  const { t } = useTranslation();
  return (filter: PortalListFilter): string => {
    switch (filter) {
      case "OPEN":
        return t("Open procurements");
      case "IN_PROGRESS":
        return t("Procurements in progress");
      case "CLOSED":
        return t("Closed procurements");
    }
  };
};

export const usePortalListEmptyText = () => {
  const { t } = useTranslation();
  return (filter: PortalListFilter): string => {
    switch (filter) {
      case "OPEN":
        return t("No public procurements are available right now");
      case "IN_PROGRESS":
        return t("No procurements are in progress right now");
      case "CLOSED":
        return t("No procurements have closed yet");
    }
  };
};

export const PortalListFilterChips = ({
  active,
  counts,
  onChange,
}: {
  active: PortalListFilter;
  counts: Partial<Record<PortalLifecycleStatus, number>>;
  onChange: (filter: PortalListFilter) => void;
}) => {
  const { t } = useTranslation();
  const label = (filter: PortalListFilter): string => {
    switch (filter) {
      case "OPEN":
        return t("Open_procurements");
      case "IN_PROGRESS":
        return t("Ongoing_procurements");
      case "CLOSED":
        return t("Closed");
    }
  };
  return (
    <Wrap spacing="2" role="group" aria-label={t("Competition status") ?? "Competition status"}>
      {PORTAL_LIST_FILTER_ORDER.map((filter) => (
        <WrapItem key={filter}>
          <FilterChip
            active={active === filter}
            label={`${label(filter)} (${countFor(filter, counts)})`}
            onClick={() => onChange(filter)}
          />
        </WrapItem>
      ))}
    </Wrap>
  );
};
